import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { FileText, ArrowRight, Loader2, User, Shield } from "lucide-react";

const LOGO_URL = "/logo-qasesores.png";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      toast.error("Por favor completa todos los campos");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Cuenta creada exitosamente!");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear la cuenta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding (wider: 2/3) */}
      <div className="hidden lg:flex lg:w-2/3 bg-[#374151] p-12 flex-col justify-between">
        <div>
          <img 
            src={LOGO_URL} 
            alt="QAsesores Logo" 
            className="h-72 w-auto"
            data-testid="qasesores-logo"
          />
        </div>
        
        <div className="space-y-6">
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Manrope'] tracking-tight leading-tight">
            Únete a <span className="text-[#45E69A]">QAsesores</span>
          </h1>
          <p className="text-lg text-slate-300 leading-relaxed max-w-md">
            Crea tu cuenta gratuita y comienza a comparar pólizas de seguros de manera inteligente.
          </p>
          
          <div className="space-y-3 pt-4">
            <div className="flex items-center gap-3 text-slate-300">
              <div className="w-8 h-8 rounded-full bg-[#0C314C] flex items-center justify-center">
                <FileText className="w-4 h-4 text-[#45E69A]" />
              </div>
              <span>Sube documentos PDF y DOC</span>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="w-8 h-8 rounded-full bg-[#0C314C] flex items-center justify-center">
                <Shield className="w-4 h-4 text-[#45E69A]" />
              </div>
              <span>Análisis automático con IA</span>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="w-8 h-8 rounded-full bg-[#0C314C] flex items-center justify-center">
                <User className="w-4 h-4 text-[#45E69A]" />
              </div>
              <span>Informes ejecutivos profesionales</span>
            </div>
          </div>
        </div>
        
        <p className="text-sm text-slate-500">
          © 2024 QAsesores. Corredores de Seguros y Reaseguros.
        </p>
      </div>

      {/* Right side - Register Form (narrower: 1/3) */}
      <div className="w-full lg:w-1/3 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="QAsesores Logo" 
              className="h-32 w-auto"
            />
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold font-['Manrope'] text-[#0C314C]">
                Crear Cuenta
              </CardTitle>
              <CardDescription className="text-slate-500">
                Completa el formulario para registrarte
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700">Nombre completo</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Tu nombre"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="focus:ring-[#0C314C] focus:border-[#0C314C]"
                    data-testid="register-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="focus:ring-[#0C314C] focus:border-[#0C314C]"
                    data-testid="register-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="focus:ring-[#0C314C] focus:border-[#0C314C]"
                    data-testid="register-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700">Confirmar contraseña</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="focus:ring-[#0C314C] focus:border-[#0C314C]"
                    data-testid="register-confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#0C314C] hover:bg-[#2a4d7a] text-white font-medium"
                  disabled={loading}
                  data-testid="register-submit-button"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  {loading ? "Creando cuenta..." : "Crear Cuenta"}
                </Button>
              </form>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-slate-500">
                  ¿Ya tienes cuenta?{" "}
                  <Link to="/login" className="text-[#0C314C] hover:text-[#2a4d7a] font-medium" data-testid="login-link">
                    Inicia sesión aquí
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
